import {noop} from '@benchling/js-utils';
import React from 'react';

import {PARENT_STORABLE_ITEMS, StorableItemType} from 'constants/ModelTypeConstants';
import {GridCellSummary, SelectedParentStorable, ValidationErrors} from 'constants/SampleConstants';
import {PotentialChildStorable} from 'inventory/location-validation/ParentStorableValidationUtils';
import {isCellEmpty} from 'samples/grids/GridCellsSelector';
import GridCellsSelectorContainer from 'samples/grids/GridCellsSelectorContainer';
import * as SampleUtil from 'samples/SampleUtil';
import {
  getEmptySelectedParent,
  getValidMoveDestinationStorableTypes,
} from 'samples/storables/ParentStorableUtil';
import InlineBanner, {InlineBannerType} from 'ui-library/banners/InlineBanner';
import FieldLabel from 'ui-library/inputs/FieldLabel';

import {StorableSummaryGraphQL} from '../../graphql-fragments/inventory/__generated/StorableSummaryGraphQL';
import ParentStorableSearchableSelectTree from './storables/ParentStorableSearchableSelectTree';

interface SingleOnUpdate {
  onUpdate: (selectedParent: SelectedParentStorable) => void;
}

export type ParentStorableDataSelectorProps = Omit<BaseProps, 'onLoadGrid' | 'onSelect' | 'onRemove'> &
  SingleOnUpdate;

/**
 * ParentStorableDataSelector with single onUpdate handler for onLoadGrid/onSelect/onRemove
 */
export default function ParentStorableDataSelector({
  onUpdate,
  ...baseProps
}: Omit<BaseProps, 'onLoadGrid' | 'onSelect' | 'onRemove'> & SingleOnUpdate): JSX.Element {
  return (
    <BaseParentStorableDataSelector
      {...baseProps}
      onLoadGrid={onUpdate}
      onSelect={onUpdate}
      onRemove={() => {
        onUpdate(getEmptySelectedParent());
      }}
    />
  );
}

export interface BaseProps {
  autoFocus?: boolean;
  canSelectMultipleGridCells?: boolean;
  onLoadGrid?: (selectedParent: SelectedParentStorable) => void;
  onRemove?: () => void;
  onSelect?: (selectedParent: SelectedParentStorable) => void;
  initialGridCells?: Array<GridCellSummary>;
  /** Extra notice to place below the grid cell selector, e.g. to tell the user how many more positions they
   * need to select. */
  gridCellNotice?: JSX.Element | null;
  isRequired?: boolean;
  label?: string;
  parentStorableTypesOverride?: Array<StorableItemType>;
  registryId?: string;
  selectedParent?: SelectedParentStorable;
  storableSchema?: {id: string; registryId: string} | null;
  validationErrors?: ValidationErrors;
  allowNone?: boolean;
  /** List of child storables that will be moved into the selected location. If provided, will result in
   * rendering available space information & deprioritization. Schemas included to support checking a
   * location's allowed schemas, but if missing simply the number of items will be considered. */
  storablesToBeMoved?: Array<PotentialChildStorable> | null;
  // See comment in MoveStorablesModal
  numStorablesToBeMovedMayChange?: boolean;
  gridCellTooltipTextByPosition?: {[gridPosition: string]: string};
}

/**
 * Base component for selecting parent storable data in a modal.
 *
 * Parent storable data includes all parent storable information. This includes both the parent storable
 * and, optionally, selected grid cell(s).
 */
export function BaseParentStorableDataSelector({
  autoFocus = false,
  canSelectMultipleGridCells = false,
  onLoadGrid = noop as (selectedParent: SelectedParentStorable) => void,
  onRemove = noop,
  onSelect = noop as (selectedParent: SelectedParentStorable) => void,
  initialGridCells = [],
  isRequired = false,
  validationErrors = {} as ValidationErrors,
  ...props
}: BaseProps): JSX.Element {
  return (
    <div>
      <ParentStorableSelector
        autoFocus={autoFocus}
        error={validationErrors.parentStorableError}
        isRequired={isRequired}
        label={props.label}
        onRemove={onRemove}
        onSelect={onSelect}
        parentStorableTypesOverride={props.parentStorableTypesOverride}
        selectedParent={props.selectedParent}
        storableSchema={props.storableSchema}
        registryId={props.registryId}
        allowNone={props.allowNone}
        storablesToBeMoved={props.storablesToBeMoved}
        // See comment in MoveStorablesModal
        numStorablesToBeMovedMayChange={props.numStorablesToBeMovedMayChange}
      />
      <ParentStorableGridCellsSelector
        canSelectMultiple={canSelectMultipleGridCells}
        error={validationErrors.parentStorableGridError}
        initialGridCells={initialGridCells}
        onLoadGrid={onLoadGrid}
        onSelect={onSelect}
        selectedParent={props.selectedParent}
        gridCellNotice={props.gridCellNotice}
        gridCellTooltipTextByPosition={props.gridCellTooltipTextByPosition}
      />
    </div>
  );
}

/**
 * Labeled and error-aware wrapper for selecting only the parent storable in a modal.
 */
export function ParentStorableSelector({
  isRequired = false,
  ...props
}: ParentStorableSelectorProps): JSX.Element {
  let storableTypes: Array<StorableItemType>;
  if (props.parentStorableTypesOverride) {
    storableTypes = props.parentStorableTypesOverride;
  } else if (props.storableSchema) {
    storableTypes = getValidMoveDestinationStorableTypes(props.storableSchema);
  } else {
    storableTypes = [...PARENT_STORABLE_ITEMS] as Array<StorableItemType>;
  }

  let actualRegistryId: string | null = null;
  if (props.registryId) {
    actualRegistryId = props.registryId;
  } else if (props.storableSchema) {
    actualRegistryId = props.storableSchema.registryId;
  }

  return (
    <div>
      <FieldLabel isRequired={isRequired} label={props.label ?? 'Location'} />
      <ParentStorableSearchableSelectTree
        autoFocus={props.autoFocus}
        onRemoveStorable={props.onRemove}
        onSelectStorable={(item: StorableSummaryGraphQL | null) => {
          props.onSelect({storable: item, gridCells: []});
        }}
        registryId={actualRegistryId}
        selectedStorable={props.selectedParent ? props.selectedParent.storable : null}
        storableTypes={storableTypes}
        allowNone={props.allowNone}
        storablesToBeMoved={props.storablesToBeMoved}
        // See comment in MoveStorablesModal
        numStorablesToBeMovedMayChange={props.numStorablesToBeMovedMayChange}
      />
      {props.error && (
        <div className="u-small-spacing-top">
          <InlineBanner bannerType={InlineBannerType.ERROR} text={props.error} />
        </div>
      )}
    </div>
  );
}

export interface ParentStorableSelectorProps {
  autoFocus?: boolean;
  error?: string | null;
  isRequired?: boolean;
  label?: string;
  onRemove: () => void;
  onSelect: (selectedParent: SelectedParentStorable) => void;
  parentStorableTypesOverride?: Array<StorableItemType>;
  registryId?: string | null;
  selectedParent?: SelectedParentStorable | null;
  storableSchema?: {id: string; registryId: string} | null;
  allowNone?: boolean;
  storablesToBeMoved?: Array<PotentialChildStorable> | null;
  // See comment in MoveStorablesModal
  numStorablesToBeMovedMayChange?: boolean;
}

/**
 * Labeled and error-aware wrapper for selecting only the optional grid cell(s) in a modal.
 */
export function ParentStorableGridCellsSelector({
  canSelectMultiple = false,
  error,
  gridCellNotice,
  gridCellTooltipTextByPosition,
  initialGridCells = [],
  onLoadGrid,
  onSelect,
  selectedParent,
}: ParentStorableGridCellsSelectorProps): JSX.Element | null {
  const isGridSelected = Boolean(
    selectedParent && selectedParent.storable && SampleUtil.isGridId(selectedParent.storable.id)
  );
  if (!isGridSelected) {
    return null;
  }
  return (
    <div className="u-spacing-top">
      <FieldLabel isRequired={true} label="Select an available position:" />
      <GridCellsSelectorContainer
        canSelectMultiple={canSelectMultiple}
        checkCell={isCellEmpty}
        className="selectParentStorableModal-gridCellContainer"
        gridId={selectedParent!.storable!.id}
        invalidCellMessage="Please select an empty position."
        onLoadGrid={() => {
          onLoadGrid({...selectedParent!, gridCells: initialGridCells});
        }}
        onSelectCells={(selectedCells: Array<GridCellSummary>) => {
          onSelect({...selectedParent!, gridCells: selectedCells});
        }}
        selectedCells={selectedParent!.gridCells}
        gridCellTooltipTextByPosition={gridCellTooltipTextByPosition}
      />
      {gridCellNotice}
      {error && (
        <div className="u-small-spacing-top">
          <InlineBanner bannerType={InlineBannerType.ERROR} text={error} />
        </div>
      )}
    </div>
  );
}

interface ParentStorableGridCellsSelectorProps {
  canSelectMultiple?: boolean;
  error?: string;
  initialGridCells?: Array<GridCellSummary>;
  onLoadGrid: (selectedParent: SelectedParentStorable) => void;
  onSelect: (selectedParent: SelectedParentStorable) => void;
  selectedParent?: SelectedParentStorable;
  /** Extra notice to place below the grid cell selector, e.g. to tell the user how many more positions they
   * need to select. */
  gridCellNotice?: JSX.Element | null;
  gridCellTooltipTextByPosition?: {[gridPosition: string]: string};
}
